import { SkyButton, SkyMessageBar, SkyText } from "@skypointcloud/skypoint-ui";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import type { MeMfa, SignInStep } from "@shared/types/mfa";
import { SingleCheckbox } from "@/components/ui/SingleCheckbox";
import { ME_QUERY_KEY } from "@/hooks/use-me";
import { apiRequest } from "@/lib/api";
import { useAuth } from "@/lib/auth/AuthContext";
import {
  clearStepUpAttempt,
  noteStepUpAttempt,
  providerDidNotAskMessage,
  recentStepUpAttempt,
  stageRememberBrowser,
  takeRememberBrowser,
} from "@/lib/auth/mfaStepUp";
import { signInErrorCode } from "@/lib/auth/signInErrors";
import { logger } from "@/lib/logger";

/**
 * Admin MFA (design 4.3): the whole page when every call but `/me` would be refused. `/me` says
 * `signInStep: "mfa"` (the mode forces this person and the sign-in proved no second factor) or
 * `"reauth"` (an admin's sign-in is older than the admin session cap, owner answer W3-9).
 *
 * "Sign in again" asks the provider with `prompt: "login"`, which evaluates its Conditional Access
 * policy again. If the provider comes back without a second factor, this page says so and offers
 * no second redirect (design 4.3, "No loop"); signing out stays possible.
 */
export function SignInStepPage({ step, mfa }: { step: SignInStep; mfa: MeMfa | undefined }) {
  const { account, reauthenticate, signOut } = useAuth();
  const client = useQueryClient();
  const accountEmail = account?.email ?? null;
  const [remember, setRemember] = useState(false);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState<string | null>(null);
  // B-57: a reauth step whose issuer never says when the person signed in cannot be satisfied by
  // signing in again, so no redirect is offered; the operator has to fix the issuer.
  const reauthImpossible = step === "reauth" && mfa?.authTimeKnown === false;
  const blocked = (step === "mfa" && recentStepUpAttempt(accountEmail)) || reauthImpossible;
  const rememberDays = mfa?.rememberDays ?? 0;

  async function signInAgain() {
    setFailed(null);
    if (step === "mfa") {
      noteStepUpAttempt(accountEmail);
      if (remember) stageRememberBrowser(accountEmail);
    }
    setBusy(true);
    try {
      const outcome = await reauthenticate();
      if (outcome === "redirecting") return;
      if (outcome === "failed") {
        setFailed("Your identity was not confirmed. Try again, or sign out.");
        return;
      }
      await client.invalidateQueries({ queryKey: ME_QUERY_KEY });
    } catch (cause) {
      // Nobody was sent to the provider: the "just asked" marker must not block the next try.
      clearStepUpAttempt();
      // The code only (#2006): Microsoft's text names the directory and the resource.
      logger.warn("the sign-in step could not be started", {
        "skydata.reason": signInErrorCode(cause) ?? "unknown",
      });
      setFailed("Signing in again could not be started. Try again, or sign out.");
    } finally {
      setBusy(false);
    }
  }

  const { title, message } = reauthImpossible
    ? {
        title: "Your sign-in cannot be confirmed",
        message:
          "Your identity provider does not say when you last signed in, so skyData cannot confirm a recent admin sign-in. Signing in again will not help. Ask your administrator to add the auth_time claim to this app's tokens, then sign out.",
      }
    : stepCopy(step, blocked, mfa);

  return (
    <div className="min-h-screen bg-background" data-testid="sign-in-step">
      <div className="mx-auto max-w-2xl space-y-4 p-6 pt-20">
        <SkyMessageBar
          intent="warning"
          layout="multiline"
          testId={`sign-in-step-${step}`}
          title={title}
          message={message}
        />
        {failed ? (
          <SkyMessageBar
            intent="error"
            layout="multiline"
            testId="sign-in-step-error"
            title="We could not confirm your identity"
            message={failed}
          />
        ) : null}
        {step === "mfa" && !blocked && rememberDays > 0 ? (
          <SingleCheckbox
            label={`Do not ask again on this browser for ${rememberDays} days`}
            checked={remember}
            onChange={setRemember}
            testId="sign-in-step-remember"
          />
        ) : null}
        {step === "mfa" && !blocked && rememberDays > 0 ? (
          <SkyText as="p" size={200} color="muted">
            Only tick this on a browser that only you use.
          </SkyText>
        ) : null}
        <div className="flex gap-2">
          {blocked ? null : (
            <SkyButton
              appearance="primary"
              testId="sign-in-step-continue"
              disabled={busy}
              onClick={() => void signInAgain()}
            >
              Sign in again
            </SkyButton>
          )}
          <SkyButton testId="sign-in-step-sign-out" onClick={() => void signOut("user")}>
            Sign out
          </SkyButton>
        </div>
      </div>
    </div>
  );
}

/** The bar's title and sentence for this step; `blocked` is an MFA step the provider skipped. */
function stepCopy(
  step: SignInStep,
  blocked: boolean,
  mfa: MeMfa | undefined,
): { title: string; message: string } {
  if (step !== "mfa") {
    return {
      title: "Sign in again to continue",
      message: `For security, an admin sign-in lasts at most ${hoursLabel(mfa)}. Sign in again to continue.`,
    };
  }
  if (blocked) {
    return {
      title: "No second sign-in step was asked for",
      message: providerDidNotAskMessage(mfa),
    };
  }
  return {
    title: "A second sign-in step is needed",
    message:
      "Your organization requires admins to complete a second sign-in step, such as a code sent to your email. Sign in again to continue.",
  };
}

function hoursLabel(mfa: MeMfa | undefined): string {
  const hours = mfa?.adminSessionMaxHours ?? 12;
  return hours === 1 ? "1 hour" : `${hours} hours`;
}

/**
 * After a sign-in that proved MFA by its token: clear the step-up marker, and remember this
 * browser when the person ticked "Do not ask again" before the redirect. Mounted by the shell.
 */
export function useRememberBrowserAfterStepUp(mfa: MeMfa | undefined): void {
  const { account } = useAuth();
  const accountEmail = account?.email ?? null;
  useEffect(() => {
    if (!mfa?.proven) return;
    clearStepUpAttempt();
    if (mfa.source !== "token" || mfa.rememberDays <= 0) return;
    if (!takeRememberBrowser(accountEmail)) return;
    void apiRequest("/api/v2/auth/mfa/remembered-device", { method: "POST", body: {} })
      .then(() => logger.info("this browser is remembered for the second sign-in step"))
      .catch((cause: unknown) =>
        logger.warn("this browser could not be remembered", { "skydata.cause": String(cause) }),
      );
  }, [mfa?.proven, mfa?.source, mfa?.rememberDays, accountEmail]);
}
